/**
 * <SpeechBubble> — ce que dit un champion en pied, dans une bulle qui pointe vers lui (molecule).
 *
 * L'ancienne bulle des champions (`SpeechBubbleM`) : l'intro de la candidature, la carte de l'étape
 * du moment, la question d'un entretien. La queue pointe vers le champion — vers le bas quand il est dessous (téléphone), vers la
 * gauche ou la droite quand il est à côté (grand écran). Une bulle ne se pose qu'à côté d'un champion
 * en pied (`<ChampionFigure>`) ; à côté d'une tête, c'est `<ChampionSays>`.
 *
 * `stepKey` fait entrer la phrase quand elle change (fondu et 12 px, `<StepStage>`) : la bulle reste,
 * ce qu'elle dit change.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { StepStage } from "../../atoms/StepStage"
import { Text } from "../../atoms/typography/Text"
import { type SpacingKey } from "../../theme/tokens/spacing"

export type SpeechBubbleTail = "down" | "left" | "right"

export type SpeechBubbleProps = {
    children: React.ReactNode
    /** Où est le champion. Défaut `down`. */
    tail?: SpeechBubbleTail
    /** `lg` pour une intro, où la phrase est tout l'écran ; `md` dans une carte. Défaut `md`. */
    size?: "md" | "lg"
    /** La phrase change : elle entre. */
    stepKey?: string
    testID?: string
}

const TAIL = 14

/**
 * À côté d'un champion debout, la bulle se lève de cette marche au-dessus de ses pieds : sa queue (à
 * 28 px du haut) arrive à hauteur de sa tête. Pour un champion de 112 à 132 px.
 */
export const SPEECH_BUBBLE_LIFT: SpacingKey = "8"

export function SpeechBubble(props: SpeechBubbleProps) {
    const { children, tail = "down", size = "md", stepKey, testID } = props
    const content =
        typeof children === "string" ? (
            <Text
                size={size === "lg" ? "text-xl" : "text-md"}
                weight={size === "lg" ? "semibold" : "regular"}
                color="text-primary"
            >
                {children}
            </Text>
        ) : (
            children
        )

    return (
        <View testID={testID} style={styles.wrap(tail)}>
            <View style={styles.bubble(size)}>
                {stepKey != null ? <StepStage stepKey={stepKey}>{content}</StepStage> : content}
            </View>
            <View style={styles.tail(tail)} />
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    wrap: (tail: SpeechBubbleTail) => ({
        paddingBottom: tail === "down" ? TAIL / 2 : 0,
        paddingLeft: tail === "left" ? TAIL / 2 : 0,
        paddingRight: tail === "right" ? TAIL / 2 : 0,
    }),
    bubble: (size: "md" | "lg") => ({
        backgroundColor: theme.colors["surface-raised"],
        borderRadius: theme.radius.lg,
        borderWidth: 1.5,
        borderColor: theme.colors["border-card"],
        paddingHorizontal: size === "lg" ? theme.spacing["5"] : theme.spacing["4"],
        paddingVertical: size === "lg" ? theme.spacing["5"] : theme.spacing["3"],
        zIndex: 1,
    }),
    // Un carré tourné, à la bordure de la bulle sur deux côtés : la pointe déborde, le reste est
    // caché sous la bulle.
    tail: (tail: SpeechBubbleTail) => ({
        position: "absolute",
        width: TAIL,
        height: TAIL,
        backgroundColor: theme.colors["surface-raised"],
        borderColor: theme.colors["border-card"],
        zIndex: 2,
        transform: [{ rotate: "45deg" }],
        ...(tail === "down"
            ? { bottom: 0, left: "50%", marginLeft: -TAIL / 2, borderRightWidth: 1.5, borderBottomWidth: 1.5 }
            : tail === "left"
              ? { left: 0, top: 28, borderLeftWidth: 1.5, borderBottomWidth: 1.5 }
              : { right: 0, top: 28, borderRightWidth: 1.5, borderTopWidth: 1.5 }),
    }),
}))
